/**
 * Contrato dos links dinâmicos, lidos de `GET publico/link-dinamico`. Os valores de `locais` e
 * `tipoAbertura` são os mesmos gravados no cadastro da área administrativa
 * (`carta-servicos/link-dinamico`) — o portal só lê e renderiza.
 */

/** Locais onde o link pode ser publicado, iguais aos do cadastro. */
export type LocalLinkDinamico =
  | 'Home/Acesso Rápido'
  | 'Serviços Digitais'
  | 'Todos os Serviços'
  | 'Notícias';

export const LOCAL_HOME: LocalLinkDinamico = 'Home/Acesso Rápido';
export const LOCAL_SERVICOS_DIGITAIS: LocalLinkDinamico = 'Serviços Digitais';
export const LOCAL_TODOS_SERVICOS: LocalLinkDinamico = 'Todos os Serviços';
export const LOCAL_NOTICIAS: LocalLinkDinamico = 'Notícias';

/** Comportamento escolhido no cadastro para a abertura do destino. */
export type TipoAberturaLink =
  | 'Mesma página'
  | 'Nova aba'
  | 'Modal interno'
  | 'Download arquivo';

export const TIPOS_ABERTURA: readonly TipoAberturaLink[] = [
  'Mesma página',
  'Nova aba',
  'Modal interno',
  'Download arquivo',
];

/**
 * Um item de `GET publico/link-dinamico`: só os links no ar hoje (situação "Ativo", não
 * excluídos e dentro da vigência), já na ordem do adm, com os locais em que aparecem.
 */
export interface LinkDinamicoPublicoResponse {
  codLinkDinamicoLkd: number;
  desNomeLkd: string;
  desDescricaoLkd: string;
  desTextoAlternativoLkd: string | null;
  desCorFundoLkd: string | null;
  desCorIconeLkd: string | null;
  desUrlDestinoLkd: string;
  desTipoAberturaLkd: string;
  /** Versão do ícone para o `?v=`; nula quando o link não tem ícone. */
  desVersaoIconeLkd: string | null;
  /** Versão da imagem em destaque para o `?v=`; nula quando o link não tem imagem. */
  desVersaoImagemLkd: string | null;
  locais: string[];
}

export interface LinkDinamico {
  id: number;
  nome: string;
  descricao: string;
  textoAlternativo: string;
  corFundo: string | null;
  corIcone: string | null;
  locais: readonly LocalLinkDinamico[];
  urlDestino: string;
  tipoAbertura: TipoAberturaLink;
  /** URL pública do ícone (atalho e serviço), com a versão; `null` sem ícone. */
  icone: string | null;
  /** URL pública da imagem em destaque (notícias), com a versão; `null` sem imagem. */
  imagem: string | null;
}

/**
 * Layout usado ao renderizar o link. Vem do local por padrão, mas a tela pode informar outro:
 * 'Todos os Serviços' aparece como card em categorias e como item de lista em todos-servicos.
 */
export type VarianteLinkDinamico = 'atalho' | 'servico' | 'lista' | 'noticia';

export function varianteDoLocal(local: LocalLinkDinamico): VarianteLinkDinamico {
  if (local === LOCAL_NOTICIAS) return 'noticia';
  if (local === LOCAL_HOME) return 'atalho';

  return 'servico';
}

/** Ícone de apoio quando o cadastro ainda não enviou um arquivo próprio. */
export function iconeDaAbertura(tipoAbertura: TipoAberturaLink): string {
  switch (tipoAbertura) {
    case 'Nova aba':
      return 'external-link';
    case 'Modal interno':
      return 'scan';
    case 'Download arquivo':
      return 'download';
    default:
      return 'arrow-right';
  }
}

export function rotuloDaAbertura(tipoAbertura: TipoAberturaLink): string {
  switch (tipoAbertura) {
    case 'Nova aba':
      return 'abre em nova aba';
    case 'Modal interno':
      return 'abre em uma janela sobre a página';
    case 'Download arquivo':
      return 'baixa o arquivo';
    default:
      return 'abre nesta página';
  }
}
